/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/use-scope-ag-application-picker.ts
 * FILE_ID: QB-AGGRID-APPLICATION-PICKER-001
 * PURPOSE: Bridge the AG-Grid row-actions "Apply pricelist application"
 *          button to the existing ApplicationSelectorModal. Owns the
 *          modal open/close state + the bound row's id, and translates
 *          the modal's onSelect callback into the section editor's
 *          onUpdateItem PATCH contract.
 * WHY: The legacy GridRow (LineItemGridRow.tsx) already exposes the
 *      application picker inline, but the new AG-Grid surface had no
 *      path to autofill applications — estimators had to leave the grid
 *      to the side-drawer to bind a pricelist row. This hook is what
 *      MR4 ("Pricelist takeoff with Application-driven product autofill")
 *      wires through the row-actions column.
 * FLOW:
 *   ┌──────────────────────┐    ┌──────────────────────┐    ┌──────────────────────┐
 *   │ Row-action click →   │ -> │ openForRow(rowId,    │ -> │ Modal opens with     │
 *   │ ctx.onApplyApplica.. │    │   context)           │    │ description seeded   │
 *   └──────────────────────┘    └──────────────────────┘    └──────────────────────┘
 *                                                                       ↓
 *   ┌──────────────────────┐    ┌──────────────────────┐    ┌──────────────────────┐
 *   │ onUpdateItem fires   │ <- │ handleSelect picks   │ <- │ User picks an        │
 *   │ {application_id}     │    │ the chosen app       │    │ Application          │
 *   └──────────────────────┘    └──────────────────────┘    └──────────────────────┘
 *
 *   Backend QuoteLineItemUpdateSerializer detects the application_id
 *   change and runs Application.apply_application(), which creates
 *   LineItemMaterial slots (primer / basecoat / topcoat) + sets the
 *   row's productivity_pmpd + recalculates totals. The new materials
 *   then populate the PRODUCTS sub-band columns added in MR3.
 *
 * DEPENDENCIES:
 *   - @/lib/quoteApi (QuoteLineItem, Application types)
 *
 * RELATED:
 *   - QB-AGGRID-ROWACTIONS-001 (ScopeAgGridRowActions.tsx): consumer button
 *   - QB-AGGRID-SECTION-001 (ScopeAgGridSection.tsx): renders the modal
 *   - apply_application (backend Application.apply_application): autofill
 *   - QB-AGGRID-PRODUCT-SLOT-BANDS-001 (bravada-product-slot-bands.ts):
 *     the columns the autofilled materials populate.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import type { Application, QuoteLineItem } from '@/lib/quoteApi'

export interface ApplicationPickerContext {
  description?: string
  area?: string
  substrate?: string
  exposure?: string
}

export interface ApplicationPickerState {
  /** Modal visibility flag, drives ApplicationSelectorModal.isOpen */
  isOpen: boolean
  /** ID of the row being edited, or null when the modal is closed. */
  rowId: string | null
  /** Currently bound application_id (highlights selection in the modal). */
  selectedId: string | null
  /** Hints fed into the modal's search bar so the first results are
   *  contextually relevant rather than the global most-used list. */
  context: ApplicationPickerContext
}

export interface UseScopeAgApplicationPickerArgs {
  /** Persist callback owned by ScopeAgGridSection. Routed through the
   *  same useScopeAgPersistHandlers contract as every other cell edit
   *  so the optimistic-update plumbing and React Query invalidation
   *  paths stay uniform. */
  onUpdateItem: (
    sectionId: string,
    itemId: string,
    patch: Partial<QuoteLineItem>,
  ) => void
  sectionId: string
}

/** Pure I→O: builds the modal "context" hints from the row payload
 *  without reading any external state. Exported so unit tests can
 *  hammer the mapping in isolation. */
export function buildPickerContext(
  row: QuoteLineItem | null | undefined,
): ApplicationPickerContext {
  if (!row) return {}
  return {
    description: row.description ?? undefined,
  }
}

/**
 * Hook surface:
 *   - state: pass directly to <ApplicationSelectorModal isOpen=... etc/>
 *   - openForRow(row): row-actions callback. Stores the row id +
 *     description seed and flips isOpen true.
 *   - close(): closes the modal without committing.
 *   - handleSelect(app): the modal's onSelect callback. Fires the
 *     PATCH then closes. Refuses if there's no bound row (defensive
 *     guard against modal sticking open across rows).
 *
 * All callbacks are referentially stable across renders so the
 * row-actions column doesn't re-render on every state tick.
 */
export function useScopeAgApplicationPicker(
  args: UseScopeAgApplicationPickerArgs,
) {
  const { onUpdateItem, sectionId } = args
  const [state, setState] = useState<ApplicationPickerState>({
    isOpen: false,
    rowId: null,
    selectedId: null,
    context: {},
  })

  const openForRow = useCallback((row: QuoteLineItem) => {
    if (!row?.id) return
    setState({
      isOpen: true,
      rowId: row.id,
      selectedId: row.application_id ?? null,
      context: buildPickerContext(row),
    })
  }, [])

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  // setState updater must stay pure (no side effects), so we close
  // the modal first via setState, then read the rowId off the state
  // ref captured by-closure to fire the PATCH. We capture rowId from
  // the current state snapshot — handleSelect always runs after
  // openForRow set it, so the closure is fresh.
  const stateRef = useRef(state)
  stateRef.current = state

  const handleSelect = useCallback(
    (app: Application) => {
      const rowId = stateRef.current.rowId
      // Always close the modal — even on a no-op, the user has
      // dismissed the picker by making a (possibly invalid) choice.
      setState((prev) => ({ ...prev, isOpen: false }))
      if (!rowId || !app?.id) return
      // PATCH only application_id — the backend autofills product_system,
      // productivity_pmpd, and the LineItemMaterial slot rows via
      // Application.apply_application(). Passing extra fields here
      // would race the backend autofill and clobber it.
      onUpdateItem(sectionId, rowId, {
        application_id: app.id,
      } as Partial<QuoteLineItem>)
    },
    [onUpdateItem, sectionId],
  )

  return useMemo(
    () => ({
      state,
      openForRow,
      close,
      handleSelect,
    }),
    [state, openForRow, close, handleSelect],
  )
}
